import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Package, ArrowLeft, MapPin, Calendar, User, CheckCircle, Truck, Box, Clock, QrCode, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  RECEIVED: { icon: Box, color: "bg-slate-500", dot: "bg-slate-500", ring: "ring-slate-100 dark:ring-slate-800", label: "Received" },
  SHIPPED: { icon: Package, color: "bg-blue-600", dot: "bg-blue-600", ring: "ring-blue-100 dark:ring-blue-900", label: "Shipped" },
  IN_TRANSIT: { icon: Truck, color: "bg-orange-600", dot: "bg-orange-600", ring: "ring-orange-100 dark:ring-orange-900", label: "In Transit" },
  OUT_FOR_DELIVERY: { icon: MapPin, color: "bg-purple-600", dot: "bg-purple-600", ring: "ring-purple-100 dark:ring-purple-900", label: "Out for Delivery" },
  DELIVERED: { icon: CheckCircle, color: "bg-emerald-600", dot: "bg-emerald-600", ring: "ring-emerald-100 dark:ring-emerald-900", label: "Delivered" },
};

const TrackingResult = () => {
  const { trackingNumber } = useParams();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  useEffect(() => {
    const fetchShipment = async () => {
      try { const res = await axios.get(`${API}/track/${trackingNumber}`); setShipment(res.data); }
      catch (err) { setError(err.response?.data?.detail || "Shipment not found"); }
      finally { setLoading(false); }
    };
    fetchShipment();
  }, [trackingNumber]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
      <div className="text-center"><div className="w-10 h-10 border-3 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Locating package...</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm"><div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center"><Link to="/" className="flex items-center"><img src="/logo-v2.png" alt="SHIPP SMART" className="h-14 w-auto rounded-md" /></Link><ThemeToggle /></div></header>
      <main className="max-w-2xl mx-auto px-6 py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-16 h-16 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center mx-auto mb-5"><Package className="w-8 h-8 text-red-500" strokeWidth={1.5} /></div>
          <h1 className="font-outfit font-bold text-2xl text-slate-900 dark:text-white mb-2" data-testid="error-title">Shipment Not Found</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-2">{error}</p>
          <p className="font-mono text-slate-400 dark:text-slate-500 text-sm mb-8">Tracking: {trackingNumber}</p>
          <Link to="/"><Button className="bg-orange-600 hover:bg-orange-500 text-white rounded-lg" data-testid="try-again-btn"><ArrowLeft className="w-4 h-4 mr-2" /> Try Another Number</Button></Link>
        </motion.div>
      </main>
    </div>
  );

  const currentStatus = STATUS_CONFIG[shipment.status] || STATUS_CONFIG.RECEIVED;
  const CurrentIcon = currentStatus.icon;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center"><img src="/logo-v2.png" alt="SHIPP SMART" className="h-14 w-auto rounded-md" /></Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setQrDialogOpen(true)} className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm" data-testid="qr-code-btn"><QrCode className="w-4 h-4 mr-2" /> QR Code</Button>
            <ThemeToggle />
            <Link to="/"><Button variant="ghost" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm" data-testid="new-search-btn"><ArrowLeft className="w-4 h-4 mr-2" /> New Search</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 dark:text-slate-400 mb-2">Tracking Number</p>
              <h1 className="font-mono text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" data-testid="tracking-number-display">{shipment.tracking_number}</h1>
            </div>
            <Badge className={`${currentStatus.color} text-white text-xs tracking-wider uppercase px-4 py-1.5 w-fit`} data-testid="status-badge">{currentStatus.label}</Badge>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-3 space-y-6">
            <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm overflow-hidden">
              <div className={`h-1 ${currentStatus.color}`} />
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl ${currentStatus.color}/10 flex items-center justify-center relative`}>
                      <CurrentIcon className={`w-7 h-7 ${currentStatus.color.replace('bg-', 'text-')}`} strokeWidth={1.5} />
                      {shipment.status === "IN_TRANSIT" && <span className={`absolute inset-0 rounded-xl ${currentStatus.color}/20 animate-ping`} />}
                    </div>
                    <div>
                      <p className="font-outfit font-semibold text-lg text-slate-900 dark:text-white">{shipment.status === "DELIVERED" ? "Package Delivered" : "Package " + currentStatus.label}</p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{shipment.origin} → {shipment.destination}</p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-1">Est. Delivery</p>
                    <p className="font-outfit font-semibold text-slate-900 dark:text-white" data-testid="estimated-delivery">{shipment.estimated_delivery || "Calculating..."}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {(shipment.description || shipment.original_tracking) && (
              <div className="grid sm:grid-cols-2 gap-4">
                {shipment.original_tracking && (
                  <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm border-l-4 border-l-orange-500">
                    <CardContent className="p-5">
                      <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">Original Tracking</p>
                      <p className="font-mono font-bold text-slate-900 dark:text-white text-lg" data-testid="original-tracking">{shipment.original_tracking}</p>
                      <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">Carrier tracking number</p>
                    </CardContent>
                  </Card>
                )}
                {shipment.description && (
                  <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
                    <CardContent className="p-5">
                      <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-2">Description</p>
                      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed" data-testid="shipment-description">{shipment.description}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl shadow-sm">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2.5 mb-8"><Clock className="w-5 h-5 text-slate-400" /><h3 className="font-outfit font-semibold text-sm uppercase tracking-wider text-slate-500 dark:text-slate-400">Shipment History</h3></div>
                <div className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-4 space-y-8 py-2" data-testid="status-timeline">
                  {shipment.status_history.slice().reverse().map((item, index) => {
                    const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.RECEIVED;
                    const isFirst = index === 0;
                    return (
                      <motion.div key={index} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + index * 0.08 }} className="relative pl-10">
                        <div className={`absolute -left-[11px] top-1.5 h-5 w-5 rounded-full ${isFirst ? config.dot : 'bg-slate-300 dark:bg-slate-600'} ring-4 ${isFirst ? config.ring : 'ring-white dark:ring-slate-900'}`} />
                        <p className="text-xs font-bold tracking-wider text-slate-400 uppercase">{formatDate(item.timestamp)}</p>
                        <p className="text-base font-semibold text-slate-900 dark:text-white mt-1">{config.label}</p>
                        <p className="text-slate-500 dark:text-slate-400 text-sm flex items-center gap-1.5 mt-0.5"><MapPin className="w-3.5 h-3.5 shrink-0" /> {item.location}</p>
                        {item.notes && <div className="mt-2 p-3 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg"><p className="text-orange-800 dark:text-orange-300 text-sm"><span className="font-semibold">Note:</span> {item.notes}</p></div>}
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-1 space-y-4">
            <Card className="bg-slate-900 dark:bg-slate-800 border-slate-800 dark:border-slate-700 rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4"><User className="w-4 h-4 text-slate-500" /><p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Recipient</p></div>
                <p className="font-semibold text-white text-lg" data-testid="receiver-name">{shipment.receiver_name}</p>
                <p className="text-slate-400 text-sm">{shipment.receiver_city}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 dark:bg-slate-800 border-slate-800 dark:border-slate-700 rounded-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4"><MapPin className="w-4 h-4 text-slate-500" /><p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Route</p></div>
                <p className="text-slate-300 text-sm mb-1">{shipment.origin}</p>
                <div className="flex items-center gap-2 my-2"><div className="flex-1 border-t border-dashed border-slate-700" /><Truck className="w-4 h-4 text-orange-500" /><div className="flex-1 border-t border-dashed border-slate-700" /></div>
                <p className="text-slate-300 text-sm">{shipment.destination}</p>
              </CardContent>
            </Card>
            {shipment.admin_notes && (
              <Card className="bg-slate-900 dark:bg-slate-800 border-slate-800 dark:border-slate-700 rounded-xl">
                <CardContent className="p-6"><div className="flex items-center gap-2.5 mb-3"><FileText className="w-4 h-4 text-slate-500" /><p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Notes</p></div><p className="text-slate-400 text-sm leading-relaxed">{shipment.admin_notes}</p></CardContent>
              </Card>
            )}
            <Card className="bg-slate-900 dark:bg-slate-800 border-slate-800 dark:border-slate-700 rounded-xl">
              <CardContent className="p-6">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-3">Quick Actions</p>
                <div className="space-y-2">
                  <Button variant="outline" onClick={() => setQrDialogOpen(true)} className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-sm justify-start rounded-lg"><QrCode className="w-4 h-4 mr-2" /> Share QR Code</Button>
                  <Link to="/" className="block"><Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-sm justify-start rounded-lg"><ArrowLeft className="w-4 h-4 mr-2" /> Track Another</Button></Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl"><DialogHeader><DialogTitle className="font-outfit font-bold text-center">Tracking QR Code</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center p-6"><div className="bg-white p-4 rounded-xl shadow-inner mb-4 border border-slate-100"><QRCodeSVG value={`${window.location.origin}/track/${shipment.tracking_number}`} size={200} level="H" includeMargin /></div><p className="font-mono text-lg font-bold text-slate-900 dark:text-white">{shipment.tracking_number}</p><p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Scan to track this shipment</p></div>
        </DialogContent>
      </Dialog>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-8 mt-12 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 text-center"><p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} SHIPP SMART. Precision Logistics.</p></div>
      </footer>
    </div>
  );
};

export default TrackingResult;
